import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild, Output, EventEmitter } from '@angular/core';
import { FlatpickrDirective, provideFlatpickrDefaults } from 'angularx-flatpickr';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ErrorMessagePipe } from '@app/shared/error-message.pipe';
import { Calendar, X, LucideAngularModule } from 'lucide-angular';
import { NgClass } from '@angular/common';
import { DateUtil } from '@app/shared/utils/date.util';

@Component({
  selector: 'app-date-picker',
  imports: [FlatpickrDirective, ReactiveFormsModule, ErrorMessagePipe, FormsModule, LucideAngularModule, NgClass],
  templateUrl: './date-picker.component.html',
  styleUrl: './date-picker.component.css',
  providers: [provideFlatpickrDefaults()],
  standalone: true,
})
export class DatePickerComponent implements AfterViewInit, OnInit {
  @Input() label: string = '';
  @Input() name: string = '';
  @Input() control!: FormControl;
  @Input() format: string = 'd/m/Y';
  @Input() valueFormat: string = 'Y-m-d';
  @Input() maxDate?: string | Date;
  @Input() minDate?: string | Date;

  // Flatpickr options
  flatpickrOptions = {
    allowInput: true,
    dateFormat: this.format,
    parseDate: (datestr: string, format: string) => {
      // Try flatpickr's default parsing first
      const parsed = this.flatpickr?.instance?.parseDate(datestr, format);
      if (parsed) return parsed;

      // Fall back to our manual parsing
      return this.parseManualInput(datestr);
    }
  };

  @Output() dateChanged = new EventEmitter<string>();

  @ViewChild('datePicker') datePicker!: ElementRef;
  @ViewChild(FlatpickrDirective) flatpickr!: FlatpickrDirective;

  protected readonly Object = Object;
  protected readonly Validators = Validators;
  protected readonly Calendar = Calendar;
  protected readonly X = X;

  private viewReady = false;
  private pendingWrite: Date | null | undefined;
  private pendingAttempts = 0;

  private isControlDisabled(): boolean {
    return !!this.control?.disabled;
  }

  onCalendarClick(event: Event, input: HTMLInputElement) {
    if (this.isControlDisabled()) {
      return;
    }
    event.stopPropagation();
    input.click();
  }

  ngAfterViewInit(): void {
    this.viewReady = true;

    if (this.control.value) {
      // Parse date string as local date to avoid timezone shifts
      const dateValue = this.parseDateString(this.control.value);
      if (dateValue) {
        this.pendingWrite = dateValue;
        this.flushPendingWrite();
      }
    } else if (this.pendingWrite !== undefined) {
      this.flushPendingWrite();
    }
  }

  ngOnInit(): void {
    this.control.valueChanges.subscribe((value) => {
      if (!this.viewReady) {
        this.pendingWrite = value ? this.parseDateString(value) : null;
        return;
      }

      if (this.control.value) {
        // Parse date string as local date to avoid timezone shifts
        const dateValue = this.parseDateString(this.control.value);
        if (dateValue) {
          this.pendingWrite = dateValue;
          this.flushPendingWrite();
        }
      } else {
        // Clear the date picker when value is null/empty
        this.pendingWrite = null;
        this.flushPendingWrite();
      }
    });
  }

  // Parse YYYY-MM-DD string as local date (no timezone conversion)
  private parseDateString(dateStr: string): Date | null {
    if (!dateStr) return null;

    // If it's in YYYY-MM-DD format, parse as local date
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }

    // Fallback to DateUtil for other formats
    return DateUtil.parseDate(dateStr);
  }

  private writeValueSafely(value: Date | null): void {
    const instance: any = this.flatpickr?.instance;
    if (!instance || !instance.config) {
      // FlatpickrDirective can expose an instance before it is fully initialized.
      // Defer the write until it has a config.
      this.pendingWrite = value;
      this.flushPendingWrite();
      return;
    }

    this.flatpickr.writeValue(value);
  }

  private flushPendingWrite(): void {
    if (this.pendingWrite === undefined) {
      return;
    }

    const instance: any = this.flatpickr?.instance;
    if (!this.viewReady || !instance || !instance.config) {
      if (this.pendingAttempts >= 5) {
        this.pendingWrite = undefined;
        this.pendingAttempts = 0;
        return;
      }

      this.pendingAttempts++;
      setTimeout(() => this.flushPendingWrite(), 0);
      return;
    }

    const next = this.pendingWrite;
    this.pendingWrite = undefined;
    this.pendingAttempts = 0;
    this.flatpickr.writeValue(next);
  }

  changed($event: Event) {
    if (this.isControlDisabled()) {
      $event.preventDefault();
      return;
    }

    const inputElement = $event.target as HTMLInputElement;
    const inputValue = inputElement.value.trim();

    // If input is empty, clear the control
    if (!inputValue) {
      this.control.setValue('', { emitEvent: false });
      this.control.markAsDirty();
      this.dateChanged.emit('');
      return;
    }

    // Try to parse the date using flatpickr first
    let date = this.flatpickr.instance.parseDate(inputValue);

    // If flatpickr can't parse it, try manual parsing of common formats
    if (!date) {
      const manuallyParsedDate = this.parseManualInput(inputValue);
      date = manuallyParsedDate || undefined;
    }

    if (date) {
      const formattedDate = this.flatpickr.instance.formatDate(date, this.valueFormat);
      this.control.setValue(formattedDate, { emitEvent: false });
      this.control.markAsDirty();
      this.dateChanged.emit(formattedDate);

      // Update the flatpickr display with the properly formatted date
      this.flatpickr.writeValue(date);
    }
  }

  onManualInput($event: Event) {
    if (this.isControlDisabled()) {
      $event.preventDefault();
      return;
    }

    const inputElement = $event.target as HTMLInputElement;
    const inputValue = inputElement.value.trim();

    // Don't process while flatpickr is active (user clicking calendar)
    if (this.flatpickr?.instance?.isOpen) {
      return;
    }

    // Allow immediate feedback for manual typing
    if (inputValue.length >= 8) { // Minimum viable date length
      this.processManualInput(inputValue);
    }
  }

  onBlur() {
    if (this.isControlDisabled()) {
      return;
    }

    setTimeout(() => {
      const inputElement = this.datePicker.nativeElement;
      const inputValue = inputElement.value.trim();

      // Process the input on blur if it hasn't been processed yet
      if (inputValue && inputValue !== this.getCurrentDisplayValue()) {
        this.processManualInput(inputValue);
      }

      this.control.markAsTouched();
    }, 200);
  }

  private processManualInput(inputValue: string) {
    if (!inputValue) {
      this.control.setValue('', { emitEvent: false });
      this.control.markAsDirty();
      this.dateChanged.emit('');
      return;
    }

    // Try to parse the date using our manual parser
    const date = this.parseManualInput(inputValue);

    if (date) {
      const formattedDate = this.flatpickr.instance.formatDate(date, this.valueFormat);
      this.control.setValue(formattedDate, { emitEvent: false });
      this.control.markAsDirty();
      this.dateChanged.emit(formattedDate);

      // Update the display to show the properly formatted date
      const displayDate = this.flatpickr.instance.formatDate(date, this.format);
      this.datePicker.nativeElement.value = displayDate;
    }
  }

  private getCurrentDisplayValue(): string {
    if (!this.control.value) return '';
    const date = this.parseManualInput(this.control.value) || DateUtil.parseDate(this.control.value);
    if (date) {
      return this.flatpickr.instance.formatDate(date, this.format);
    }
    return '';
  }

  clearDate() {
    this.control.setValue('', { emitEvent: false });
    this.control.markAsDirty();
    this.dateChanged.emit('');

    // Clear the flatpickr display
    if (this.flatpickr) {
      this.flatpickr.writeValue(null);
    }
  }

  public parseManualInput(input: string): Date | null {
    // Remove any extra spaces and normalize separators
    const cleaned = input.replace(/\s+/g, ' ').trim();

    // Common date format patterns
    const patterns = [
      // dd/mm/yyyy, dd-mm-yyyy, dd.mm.yyyy
      /^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/,
      // dd/mm/yy, dd-mm-yy, dd.mm.yy
      /^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2})$/,
      // yyyy-mm-dd, yyyy/mm/dd, yyyy.mm.dd
      /^(\d{4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})$/,
      // mm/dd/yyyy, mm-dd-yyyy (US format)
      /^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/,
    ];

    for (const pattern of patterns) {
      const match = cleaned.match(pattern);
      if (match) {
        let day, month, year;

        if (pattern.source.startsWith('^(\\d{4})')) {
          // yyyy-mm-dd format
          year = parseInt(match[1]);
          month = parseInt(match[2]) - 1; // Month is 0-indexed
          day = parseInt(match[3]);
        } else if (pattern.source.includes('(\\d{4})')) {
          // dd/mm/yyyy or mm/dd/yyyy format - assume dd/mm/yyyy (UK format)
          day = parseInt(match[1]);
          month = parseInt(match[2]) - 1;
          year = parseInt(match[3]);
        } else {
          // dd/mm/yy format
          day = parseInt(match[1]);
          month = parseInt(match[2]) - 1;
          year = parseInt(match[3]);
          // Convert 2-digit year to 4-digit (assume 2000s for 00-29, 1900s for 30-99)
          year = year < 30 ? 2000 + year : 1900 + year;
        }

        // Validate the date components
        if (month >= 0 && month <= 11 && day >= 1 && day <= 31 && year >= 1900 && year <= 2100) {
          const date = new Date(year, month, day);
          // Check if the date is valid (handles leap years, etc.)
          if (date.getFullYear() === year && date.getMonth() === month && date.getDate() === day) {
            return date;
          }
        }
      }
    }

    return null;
  }

}
